import { useParentGameStore, useSelectedFullRoundStore, useSelectionsStore } from '@/store/store'
import { GameType, SelectionUpdateObj } from 'types/types'
import { create } from 'zustand'

type LocalSelectionsState = {
  tiebreakers: Array<{ round_id: number; answer: string | null }>
  setTiebreakers: (tiebreaker: { round_id: number; answer: string | null }) => void
  clearTiebreakers: () => void
  clearRoundTiebreaker: (roundId: number) => void
  userPredictions: SelectionUpdateObj[]
  setUserPredictions: (predictions: SelectionUpdateObj[]) => void
  canSavePredictions: boolean
  setCanSavePredictions: (canSave: boolean) => void
  localSelections: SelectionUpdateObj[]
  setLocalSelections: (selections: SelectionUpdateObj[]) => void
  mergeLocalSelections: (selections: SelectionUpdateObj[]) => void
  clearLocalSelections: () => void
  clearRoundLocalSelections: (roundId: number) => void
  isValidTiebreaker: boolean
  setIsValidTiebreaker: (isValid: boolean) => void
  // Bumped whenever a round's local selections are discarded — fixture
  // components that keep their own "currently picked" React state (rather
  // than deriving it from localSelections on every render)
  roundRevertVersions: Record<number, number>
  bumpRoundRevertVersion: (roundId: number) => void
}

const useLocalSelectionsStore = create<LocalSelectionsState>((set) => ({
  tiebreakers: [],
  setTiebreakers: (tiebreaker: { round_id: number; answer: string | null }) =>
    set((state) => {
      const newTiebreakers = state.tiebreakers.filter((t) => t.round_id !== tiebreaker.round_id)
      return {
        tiebreakers: [...newTiebreakers, tiebreaker],
      }
    }),
  clearTiebreakers: () => set({ tiebreakers: [] }),
  clearRoundTiebreaker: (roundId: number) =>
    set((state) => ({
      tiebreakers: state.tiebreakers.filter((t) => t.round_id !== roundId),
      isValidTiebreaker: false,
    })),
  userPredictions: [],
  setUserPredictions: (predictions: SelectionUpdateObj[]) => {
    set({ userPredictions: predictions })
  },
  canSavePredictions: false,
  setCanSavePredictions: (canSave: boolean) => {
    set({ canSavePredictions: canSave })
  },
  localSelections: [],
  setLocalSelections: (selections: SelectionUpdateObj[]) => {
    set({ localSelections: selections })
  },
  mergeLocalSelections: (newSelections: SelectionUpdateObj[]) =>
    set((state) => {
      const merged = [...state.localSelections]
      newSelections.forEach((newSel) => {
        const exists = merged.some(
          (s) => s.match_id === newSel.match_id && s.unit_id === newSel.unit_id,
        )
        if (!exists) {
          merged.push(newSel)
        }
      })
      return { localSelections: merged }
    }),
  clearLocalSelections: () => {
    set({ localSelections: [] })
  },
  clearRoundLocalSelections: (roundId: number) =>
    set((state) => ({
      localSelections: state.localSelections.filter((s) => s.round_id !== roundId),
    })),
  isValidTiebreaker: false,
  setIsValidTiebreaker: (isValid: boolean) => {
    set({ isValidTiebreaker: isValid })
  },
  roundRevertVersions: {},
  bumpRoundRevertVersion: (roundId: number) =>
    set((state) => ({
      roundRevertVersions: {
        ...state.roundRevertVersions,
        [roundId]: (state.roundRevertVersions[roundId] ?? 0) + 1,
      },
    })),
}))

export const useLocalSelectionsState = () => {
  const {
    localSelections,
    tiebreakers,
    setTiebreakers,
    setLocalSelections,
    mergeLocalSelections,
    isValidTiebreaker,
    setIsValidTiebreaker,
    setUserPredictions,
    clearLocalSelections,
    clearRoundLocalSelections,
    clearTiebreakers,
    clearRoundTiebreaker,
    userPredictions,
    roundRevertVersions,
    bumpRoundRevertVersion,
  } = useLocalSelectionsStore((state) => state)
  const [currentFullRound] = useSelectedFullRoundStore((state) => [state.selectedRound])
  const [selections] = useSelectionsStore((state) => [state.selections])
  const [parentGame] = useParentGameStore((state) => [state.parentGame])
  const correctSelectionsLength = parentGame?.selection_type === GameType.SR ? 2 : 1
  const selectionsLength = Object.keys(selections?.matches ?? {}).length

  const currentRoundLocalSelections = localSelections.filter(
    (s) => s.round_id === currentFullRound?.id,
  )

  const isValidAmountOfSelections =
    selectionsLength === currentFullRound?.matches.length
      ? currentRoundLocalSelections.length > 0
      : currentRoundLocalSelections.length / correctSelectionsLength ===
        currentFullRound?.matches.length

  const currentTiebreakerAnswer = selections?.tiebreakers?.find(
    (t) => t.round_id === currentFullRound?.id,
  )

  const allMatchesPredictedOnServer = selectionsLength === currentFullRound?.matches.length

  // eslint-disable-next-line no-nested-ternary
  const canSavePredictions = currentFullRound?.tiebreaker_question
    ? currentTiebreakerAnswer?.answer
      ? isValidAmountOfSelections || isValidTiebreaker
      : (isValidAmountOfSelections || allMatchesPredictedOnServer) && isValidTiebreaker
    : isValidAmountOfSelections

  const localTiebreakerAnswer =
    tiebreakers.find((t) => t.round_id === currentFullRound?.id)?.answer ?? ''
  const hasUnsavedTiebreakerChange =
    localTiebreakerAnswer !== '' &&
    Number(localTiebreakerAnswer) !== currentTiebreakerAnswer?.answer

  // A local entry with no saved counterpart that also matches SR's untouched
  // auto-seed shape ({ score: 0, winner: null }, merged in so a round is
  // submittable without clicking every match's controls) isn't a genuine
  // edit — just landing on this page auto-seeds it. Anything else (a real
  // click, or an edit that actually differs from what's saved) counts.
  const hasGenuineLocalSelectionChange = currentRoundLocalSelections.some((local) => {
    const savedCounterpart = selections?.matches?.[local.match_id]?.selections.find(
      (saved) => saved.unit_id === local.unit_id,
    )
    if (savedCounterpart) return savedCounterpart.score !== local.score
    return !(local.score === 0 && local.winner === null)
  })

  const hasUnsavedChanges = hasGenuineLocalSelectionChange || hasUnsavedTiebreakerChange

  return {
    tiebreakers,
    isValidTiebreaker,
    clearTiebreakers,
    setTiebreakers,
    setIsValidTiebreaker,
    isValidAmountOfSelections,
    canSavePredictions,
    userPredictions,
    setUserPredictions,
    localSelections,
    currentRoundLocalSelections,
    setLocalSelections,
    mergeLocalSelections,
    clearLocalSelections,
    clearRoundLocalSelections,
    clearRoundTiebreaker,
    currentTiebreakerAnswer,
    hasUnsavedChanges,
    roundRevertVersions,
    bumpRoundRevertVersion,
  }
}
